import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Layout from "../../Layout";
import { Form, Input, Button, Avatar, Spin, message } from "antd";
import {
  UserOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  CameraOutlined,
} from "@ant-design/icons";
import { getProfile, updateProfile, ProfileInfo } from "../../api/Profile";

const ProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [avatar, setAvatar] = useState<string>();
  const [avatarFile, setAvatarFile] = useState<File>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Chặn trình duyệt tự điền mật khẩu đã lưu: để readOnly tới khi người dùng
  // chủ động focus vào ô (autoComplete đơn thuần thường bị Chrome bỏ qua).
  const [pwReadOnly, setPwReadOnly] = useState(true);

  // Nạp thông tin hiện tại của nhân sự đang đăng nhập
  useEffect(() => {
    let active = true;
    setLoading(true);
    getProfile().then((res) => {
      if (!active) return;
      if (res.status && res.data) {
        const info: ProfileInfo = res.data;
        const name =
          info.fullname ||
          info.name ||
          [info.first_name, info.last_name].filter(Boolean).join(" ") ||
          "";
        form.setFieldsValue({
          name,
          email: info.email || "",
          phone: info.phone || "",
        });
        setAvatar(info.avatar || undefined);

        // Đồng bộ vào localStorage + báo header cập nhật (avatar/tên) ngay khi mở trang
        try {
          const stored = JSON.parse(localStorage.getItem("user") || "{}");
          localStorage.setItem(
            "user",
            JSON.stringify({
              ...stored,
              fullname: name || stored.fullname,
              email: info.email || stored.email,
              phone: info.phone || stored.phone,
              ...(info.avatar ? { avatar: info.avatar } : {}),
            })
          );
          window.dispatchEvent(new Event("user-updated"));
        } catch {
          /* bỏ qua nếu user trong localStorage không hợp lệ */
        }
      } else {
        message.error(t("profile.load_failed"));
      }
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [form, t]);

  const MAX_AVATAR_SIZE = 5 * 1024 * 1024; // 5MB

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // reset input để chọn lại cùng một file vẫn kích hoạt onChange
    e.target.value = "";
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      message.error(t("profile.avatar_invalid_type"));
      return;
    }
    if (file.size > MAX_AVATAR_SIZE) {
      message.error(t("profile.avatar_too_large"));
      return;
    }

    setAvatarFile(file);
    setAvatar(URL.createObjectURL(file)); // xem trước ngay
  };

  const handleSave = async (values: any) => {
    const changingPassword =
      values.current_pass || values.new_pass || values.confirm_pass;

    setSaving(true);
    const res = await updateProfile({
      name: values.name?.trim(),
      email: values.email?.trim(),
      phone: values.phone?.trim(),
      ...(avatarFile ? { avatar: avatarFile } : {}),
      ...(changingPassword
        ? {
            current_pass: values.current_pass,
            new_pass: values.new_pass,
            confirm_pass: values.confirm_pass,
          }
        : {}),
    });
    setSaving(false);

    if (res.status) {
      message.success(t("profile.update_success"));

      // Avatar mới: ưu tiên đường dẫn server trả về, nếu không thì giữ ảnh xem trước
      const newAvatar = res.data?.avatar || avatar;
      if (res.data?.avatar) setAvatar(res.data.avatar);
      setAvatarFile(undefined);

      // Đồng bộ lại localStorage để header/giao diện phản ánh thông tin mới
      try {
        const stored = JSON.parse(localStorage.getItem("user") || "{}");
        localStorage.setItem(
          "user",
          JSON.stringify({
            ...stored,
            fullname: values.name?.trim() || stored.fullname,
            email: values.email?.trim() || stored.email,
            phone: values.phone?.trim() || stored.phone,
            ...(newAvatar ? { avatar: newAvatar } : {}),
          })
        );
        window.dispatchEvent(new Event("user-updated"));
      } catch {
        /* bỏ qua nếu user trong localStorage không hợp lệ */
      }

      // Xóa các ô mật khẩu sau khi lưu
      form.setFieldsValue({
        current_pass: "",
        new_pass: "",
        confirm_pass: "",
      });
    } else {
      message.error(res.msg || t("profile.update_failed"));
    }
  };

  return (
    <Layout>
      <div
        className="
          bg-white
          dark:bg-[#1f2937]
          border
          border-slate-200
          dark:border-slate-700
          rounded-lg
          p-6
        "
      >
        {/* Header */}
        <h2 className="text-xl font-semibold text-slate-800 dark:text-white mb-6">
          {t("profile.title")}
        </h2>

        <Spin spinning={loading}>
          <Form layout="vertical" form={form} onFinish={handleSave}>
            {/* Thông tin cơ bản */}
            <div className="border-b border-slate-200 dark:border-slate-700 pb-6">
              <h3 className="font-semibold text-slate-800 dark:text-white mb-4">
                {t("profile.basic_info")}
              </h3>

              {/* Avatar (hiển thị) */}
              <div
                className="
                  bg-slate-50
                  dark:bg-slate-800
                  rounded-lg
                  p-5
                  flex
                  items-center
                  gap-5
                  mb-6
                "
              >
                <div
                  className="relative group cursor-pointer rounded-full overflow-hidden shrink-0"
                  style={{ width: 80, height: 80 }}
                  onClick={() => fileInputRef.current?.click()}
                  title={t("profile.avatar_change")}
                >
                  <Avatar size={80} src={avatar} icon={<UserOutlined />} />
                  <div
                    className="
                      absolute inset-0
                      bg-black/45 text-white
                      flex items-center justify-center
                      opacity-0 group-hover:opacity-100 transition
                    "
                  >
                    <CameraOutlined style={{ fontSize: 22 }} />
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleAvatarChange}
                  />
                </div>
                <div>
                  <div className="font-semibold text-slate-800 dark:text-white">
                    {t("profile.avatar")}
                  </div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">
                    {t("profile.avatar_hint")}
                  </div>
                </div>
              </div>

              {/* Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6">
                <Form.Item
                  label={t("profile.name")}
                  name="name"
                  rules={[
                    { required: true, message: t("profile.name_required") },
                  ]}
                >
                  <Input size="large" placeholder={t("profile.name_placeholder")} />
                </Form.Item>

                <Form.Item
                  label={t("profile.phone")}
                  name="phone"
                >
                  <Input size="large" placeholder={t("profile.phone_placeholder")} />
                </Form.Item>

                <Form.Item
                  label={t("profile.email")}
                  name="email"
                  rules={[
                    { type: "email", message: t("profile.email_invalid") },
                  ]}
                >
                  <Input size="large" placeholder={t("profile.email_placeholder")} />
                </Form.Item>
              </div>
            </div>

            {/* Đổi mật khẩu */}
            <div className="py-6">
              <h3 className="font-semibold text-slate-800 dark:text-white mb-1">
                {t("profile.change_password")}
              </h3>
              <div className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                {t("profile.change_password_hint")}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Form.Item
                  label={t("profile.current_password")}
                  name="current_pass"
                  dependencies={["new_pass", "confirm_pass"]}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const changing =
                          getFieldValue("new_pass") ||
                          getFieldValue("confirm_pass");
                        if (changing && !value) {
                          return Promise.reject(
                            new Error(t("profile.current_password_required"))
                          );
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    size="large"
                    autoComplete="new-password"
                    readOnly={pwReadOnly}
                    onFocus={() => setPwReadOnly(false)}
                    iconRender={(visible) =>
                      visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                    }
                  />
                </Form.Item>

                <Form.Item
                  label={t("profile.new_password")}
                  name="new_pass"
                  dependencies={["current_pass", "confirm_pass"]}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const changing =
                          getFieldValue("current_pass") ||
                          getFieldValue("confirm_pass");
                        if (changing && !value) {
                          return Promise.reject(
                            new Error(t("profile.new_password_required"))
                          );
                        }
                        if (value && value.length < 6) {
                          return Promise.reject(
                            new Error(t("profile.new_password_min"))
                          );
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    size="large"
                    autoComplete="new-password"
                    readOnly={pwReadOnly}
                    onFocus={() => setPwReadOnly(false)}
                    iconRender={(visible) =>
                      visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                    }
                  />
                </Form.Item>

                <Form.Item
                  label={t("profile.confirm_password")}
                  name="confirm_pass"
                  dependencies={["new_pass"]}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const newPass = getFieldValue("new_pass");
                        if (newPass && !value) {
                          return Promise.reject(
                            new Error(t("profile.confirm_password_required"))
                          );
                        }
                        if (value && value !== newPass) {
                          return Promise.reject(
                            new Error(t("profile.confirm_password_mismatch"))
                          );
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    size="large"
                    autoComplete="new-password"
                    readOnly={pwReadOnly}
                    onFocus={() => setPwReadOnly(false)}
                    iconRender={(visible) =>
                      visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                    }
                  />
                </Form.Item>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 pt-6 border-t border-slate-200 dark:border-slate-700">
              <Button
                size="large"
                disabled={saving}
                onClick={() =>
                  form.setFieldsValue({
                    current_pass: "",
                    new_pass: "",
                    confirm_pass: "",
                  })
                }
              >
                {t("profile.cancel_password")}
              </Button>

              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={saving}
              >
                {t("profile.save")}
              </Button>
            </div>
          </Form>
        </Spin>
      </div>
    </Layout>
  );
};

export default ProfilePage;
